import React, { useCallback, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { fade } from '@material-ui/core/styles/colorManipulator';
import Autocomplete from '@material-ui/lab/Autocomplete';
import DeleteIcon from '@material-ui/icons/Delete';
import { PermitCarIcon } from '../../../../Icons';
import VppDatePicker from '../../../../components/datePicker/vppDatePicker';
import US_STATES_WITH_INITIALS from '../../../../us_states_and_initials';
import moment from 'moment-timezone';
import { useSelector } from 'react-redux';
import PermitCardActions from './PermitCardActions';
import PermitCardView from './PermitCardView';
import UnitStatusMetaField from './UnitStatusMetaField';
import {
  mergeVehicleFields,
  normalizeLicenceState,
} from './licenceStateUtils';
const licencePlateValidator = {
  required: 'License plate is required.',
  maxLength: {
    value: 11,
    message: 'License plate must be 11 or less characters',
  },
  pattern: {
    value: /^[A-Z0-9]*$/,
    message:
      'License plate must be only capital numbers and letters without especial chars',
  },
};

moment.tz.setDefault('America/Chicago');

const useStyles = makeStyles((theme) => ({
  // Label + control on one row; top-align so helper/error text does not
  // pull the title down below the input (Status-style layout).
  fieldLabel: {
    flexShrink: 0,
    lineHeight: 1.2,
    minWidth: 110,
    marginRight: theme.spacing(1),
    // Optically center caption against size="small" outlined input (~36px)
    paddingTop: 9,
    color: theme.palette.text.secondary,
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
    marginBottom: theme.spacing(0.75),
  },
  fieldControl: {
    flex: 1,
    marginTop: 0,
    minWidth: 0,
    '& .MuiFormControl-root': {
      marginTop: 0,
      marginBottom: 0,
      width: '100%',
    },
    '& .MuiTextField-root': {
      marginTop: 0,
      marginBottom: 0,
      width: '100%',
    },
    // Same soft fill as License Plate for every edit control on the card
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#F7F8FC !important',
    },
    '& .MuiOutlinedInput-input': {
      backgroundColor: '#F7F8FC !important',
    },
    '& .MuiAutocomplete-inputRoot': {
      backgroundColor: '#F7F8FC !important',
    },
    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
      borderColor: '#D0D5DD !important',
      borderWidth: '1px !important',
      boxShadow: 'none !important',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#D0D5DD !important',
      borderWidth: '1px !important',
      boxShadow: 'none !important',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#D0D5DD !important',
      borderWidth: '1px !important',
      boxShadow: 'none !important',
    },
    // Theme Select uses a dark outline — strip it inside permit edit fields
    '& .MuiSelect-outlined': {
      border: '0 !important',
      background: 'transparent !important',
      boxShadow: 'none !important',
    },
    // Pull calendar icon right to match Status select chevron (right: 7)
    '& .MuiOutlinedInput-adornedEnd': {
      paddingRight: 8,
    },
    '& .MuiInputAdornment-positionEnd': {
      marginLeft: 0,
      marginRight: 0,
    },
    '& .MuiInputAdornment-positionEnd .MuiIconButton-root': {
      padding: 4,
      marginRight: -4,
    },
  },
  editBody: {
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(0.5),
  },
  fieldRowLast: {
    marginBottom: 0,
  },
  cardHeader: {
    backgroundColor: fade(theme.palette.primary.main, 0.06),
    // Keep view/edit header height stable when delete icon appears
    minHeight: 40,
    boxSizing: 'border-box',
  },
  vehicleTitleRow: {
    display: 'flex',
    alignItems: 'center',
  },
  vehicleIcon: {
    width: 18,
    height: 18,
    marginRight: theme.spacing(0.75),
    flexShrink: 0,
    display: 'block',
    objectFit: 'contain',
    transform: 'translateY(-1px)',
  },
  vehicleTitle: {
    color: '#4796FB',
    fontWeight: 600,
    fontSize: 13,
    lineHeight: 1,
    margin: 0,
    display: 'inline-flex',
    alignItems: 'center',
  },
  headerStatus: {
    marginLeft: 'auto',
    flexShrink: 0,
  },
  deleteIconButton: {
    padding: 2,
    marginLeft: 'auto',
    alignSelf: 'center',
  },
  deleteIcon: {
    fontSize: 20,
  },
}));

const PermitFieldLabel = ({ children, className, style, color = 'inherit' }) => (
  <Typography
    variant="caption"
    color={color === 'secondary' ? 'secondary' : 'inherit'}
    className={className}
    display="block"
    style={style}
  >
    {children}
  </Typography>
);

const resolveVehicleAtIndex = (vehicles, index) => {
  if (!vehicles) {
    return {};
  }

  if (Array.isArray(vehicles)) {
    return vehicles[index] || {};
  }

  if (typeof vehicles === 'object') {
    return vehicles[index] ?? vehicles[String(index)] ?? {};
  }

  return {};
};

/** Issue date = day the permit slot was added. */
const defaultIssueDate = () => moment().toDate();

/**
 * Guest UI default expiry — same cutoff rule as base VehiclesBlock / VehicleForm.
 * Uses guest_hours_finish: before cutoff → today; at/after → tomorrow.
 * 24h window (start === finish) → always +1 day.
 */
const getGuestDefaultExpirationDate = (
  guest_hours_start,
  guest_hours_finish
) => {
  if (!guest_hours_start || !guest_hours_finish) {
    return moment().add(1, 'days').toDate();
  }

  const currentTime = moment();
  const time = guest_hours_finish.split(':');

  if (guest_hours_start === guest_hours_finish) {
    return currentTime.add(1, 'days').toDate();
  }

  const targetTime = moment().set({
    hours: time[0],
    minutes: time[1],
    seconds: time[2] || 0,
  });

  return currentTime.format('HH:mm') >= guest_hours_finish
    ? targetTime.add(1, 'days').toDate()
    : targetTime.toDate();
};

const defaultExpirationFromIssue = (
  vehicleType,
  issueDate = defaultIssueDate(),
  guest_hours_start,
  guest_hours_finish
) => {
  if (vehicleType === 'tenant') {
    // e.g. issue 2026-08-20 → expiry 2027-08-20
    return moment(issueDate).startOf('day').clone().add(1, 'years').toDate();
  }
  return getGuestDefaultExpirationDate(guest_hours_start, guest_hours_finish);
};

const UnitsVehiclesBlock = ({
  fieldId = null,
  vehicleNumber,
  vehicleIndex,
  vehicleTitle,
  type,
  vehicle = null,
  control,
  register,
  fieldErrors,
  deleteButtonHandler,
  watch,
  getValues,
  formErrors,
  setValue,
  showActions,
  unitArchived = false,
  submitDisabled,
  isUnitLoaded = false,
  startInEditMode = false,
  onSave,
  onCancel,
}) => {
  const classes = useStyles();
  const rules = useSelector((state) => state.rules);
  const { guest_hours_finish, guest_hours_start } = rules || {};
  const [isEditing, setIsEditing] = useState(startInEditMode);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const wasEditingRef = useRef(isEditing);
  const disabled = !isEditing;
  const statusSelect = ['pending', 'active', 'blocked', 'suspended'].map(
    (i) => ({
      name: i.charAt(0).toUpperCase() + i.slice(1),
      value: i,
    })
  );
  const inputNamePrefix =
    type === 'tenant' ? 'tenant_vehicles' : 'guest_vehicles';

  const inputName = (name) => `${inputNamePrefix}[${vehicleIndex}].${name}`;
  const today = defaultIssueDate();

  const isEmptyVehicleSlot = (data) =>
    !data?.id &&
    !data?.licence_plate &&
    !data?.make &&
    !data?.model &&
    !data?.color &&
    !normalizeLicenceState(data?.licence_state);

  const getCurrentVehicleData = useCallback(() => {
    const baseVehicle =
      vehicle && typeof vehicle === 'object' ? vehicle : {};
    const vehiclesFromWatch = watch(inputNamePrefix);
    const vehiclesFromForm = getValues ? getValues(inputNamePrefix) : null;
    const formVehicle = resolveVehicleAtIndex(vehiclesFromForm, vehicleIndex);
    const watchedVehicle = resolveVehicleAtIndex(
      vehiclesFromWatch,
      vehicleIndex
    );

    // Empty padded slots must stay blank after another card saves and shifts
    // to the front. Ignore leftover form/watch values until the user is editing.
    // View mode shows defaults: pending, issue=today, expiry tenant+1y / guest+1d.
    // Edit mode must also start blank (+ defaults) — never merge stale form/watch
    // from the permit that used to occupy this index.
    if (isEmptyVehicleSlot(baseVehicle)) {
      const issueDate = defaultIssueDate();
      return {
        status: 'pending',
        issue_date: issueDate,
        expiration_date: defaultExpirationFromIssue(
          type,
          issueDate,
          guest_hours_start,
          guest_hours_finish
        ),
        licence_plate: '',
        licence_state: '',
        make: '',
        model: '',
        color: '',
      };
    }

    return mergeVehicleFields(baseVehicle, formVehicle, watchedVehicle);
  }, [
    getValues,
    guest_hours_finish,
    guest_hours_start,
    inputNamePrefix,
    isEditing,
    type,
    vehicle,
    vehicleIndex,
    watch,
  ]);

  const populateEditFields = useCallback(
    (data) => {
      const fields = [
        'id',
        'status',
        'expiration_date',
        'licence_plate',
        'licence_state',
        'make',
        'model',
        'color',
        'issue_date',
      ];
      // Use the slot prop, not merged form data — form may still hold values
      // from before this permit shifted to index 0 after save.
      const emptySlot = isEmptyVehicleSlot(
        vehicle && typeof vehicle === 'object' ? vehicle : {}
      );
      const issueDate = defaultIssueDate();

      fields.forEach((field) => {
        let value = data?.[field];

        if (field === 'licence_state') {
          value = normalizeLicenceState(value);
        }

        if (emptySlot) {
          if (field === 'issue_date') {
            setValue(inputName(field), issueDate);
            return;
          }
          if (field === 'status') {
            setValue(inputName(field), 'pending');
            return;
          }
          if (field === 'expiration_date') {
            setValue(
              inputName(field),
              defaultExpirationFromIssue(
                type,
                issueDate,
                guest_hours_start,
                guest_hours_finish
              )
            );
            return;
          }
          setValue(inputName(field), '');
          return;
        }

        if (value !== undefined && value !== null && value !== '') {
          setValue(inputName(field), value);
        } else if (field === 'licence_state' || field === 'licence_plate') {
          setValue(inputName(field), '');
        }
      });

      if (!data?.issue_date || emptySlot) {
        setValue(inputName('issue_date'), issueDate);
      }
    },
    [
      guest_hours_finish,
      guest_hours_start,
      setValue,
      type,
      vehicle,
      vehicleIndex,
    ]
  );

  const handleStartEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleConfirmDelete = () => {
    // Prefer the slot prop id (from unit GET). Form hidden id can be blank
    // when the card mounted before data finished loading.
    const formVehicles = getValues ? getValues(inputNamePrefix) : null;
    const formVehicle = resolveVehicleAtIndex(formVehicles, vehicleIndex);
    const merged = getCurrentVehicleData();
    const rawId =
      vehicle?.id ??
      formVehicle?.id ??
      merged?.id ??
      null;
    let permitId = null;
    if (rawId !== null && rawId !== undefined && rawId !== '') {
      const asNumber = Number(rawId);
      permitId = Number.isFinite(asNumber) && asNumber > 0 ? asNumber : rawId;
    }

    setDeleteDialogOpen(false);

    if (typeof deleteButtonHandler !== 'function') {
      return;
    }

    deleteButtonHandler(Number(vehicleIndex), type, permitId);

    if (!permitId) {
      setIsEditing(false);
    }
  };

  const errorCheck = (name) => {
    return (
      (fieldErrors[inputNamePrefix] &&
        fieldErrors[inputNamePrefix][vehicleIndex] &&
        fieldErrors[inputNamePrefix][vehicleIndex][name]) ||
      (formErrors[inputNamePrefix] &&
        formErrors[inputNamePrefix][vehicleIndex] &&
        formErrors[inputNamePrefix][vehicleIndex][name])
    );
  };

  React.useEffect(() => {
    if (startInEditMode) {
      setIsEditing(true);
    } else {
      setIsEditing(false);
    }
  }, [startInEditMode]);

  React.useEffect(() => {
    if (isEditing && !wasEditingRef.current) {
      populateEditFields(getCurrentVehicleData());
    }

    wasEditingRef.current = isEditing;
  }, [getCurrentVehicleData, isEditing, populateEditFields]);

  const errorText = (name) => {
    if (!errorCheck(name)) {
      return null;
    }
    const fromFieldErrors =
      fieldErrors?.[inputNamePrefix]?.[vehicleIndex]?.[name];
    if (Array.isArray(fromFieldErrors)) {
      return fromFieldErrors.join(',');
    }
    if (typeof fromFieldErrors === 'string') {
      return fromFieldErrors;
    }
    return formErrors?.[inputNamePrefix]?.[vehicleIndex]?.[name]?.message || null;
  };

  const renderAutocompleteTextField = (params) => {
    return (
      <TextField
        {...params}
        name={inputName('licence_state')}
        type="text"
        variant="outlined"
        margin="none"
        error={errorCheck('licence_state')}
        helperText={errorText('licence_state')}
        fullWidth
      />
    );
  };

  const defaulteExpirationDateValue = defaultExpirationFromIssue(
    type,
    today,
    guest_hours_start,
    guest_hours_finish
  );

  const viewValues = getCurrentVehicleData();
  const watchedLicenceState = watch(inputName('licence_state'));
  // Client: archived unit → show Archived on every permit card (UI only).
  const permitStatus = unitArchived
    ? 'archived'
    : String(viewValues?.status || '')
        .toLowerCase()
        .trim();
  // Prefer live form value (including intentional clear) over stale view merge.
  const licenceStateCode = normalizeLicenceState(
    watchedLicenceState !== undefined && watchedLicenceState !== null
      ? watchedLicenceState
      : viewValues.licence_state
  );

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Box
        className={classes.cardHeader}
        px={1.5}
        py={0.75}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Box className={classes.vehicleTitleRow}>
          <PermitCarIcon className={classes.vehicleIcon} width={18} height={18} />
          <Typography variant="subtitle2" className={classes.vehicleTitle} component="span">
            {vehicleTitle} {String(vehicleNumber).padStart(2, '0')}
          </Typography>
        </Box>
        {isEditing && showActions ? (
          <IconButton
            id={`delete_vehicle_button_${fieldId ?? 'unknown'}_${vehicleNumber}`}
            type="button"
            aria-label="delete"
            className={classes.deleteIconButton}
            disabled={submitDisabled}
            onMouseDown={(event) => {
              // Stop form focus / Enter-submit side effects before click.
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setDeleteDialogOpen(true);
            }}
          >
            <DeleteIcon className={classes.deleteIcon} color="secondary" />
          </IconButton>
        ) : permitStatus ? (
          <Box className={classes.headerStatus}>
            <UnitStatusMetaField status={permitStatus} size="small" />
          </Box>
        ) : (
          <Box width={30} />
        )}
      </Box>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby={`delete_vehicle_dialog_title_${fieldId ?? 'unknown'}_${vehicleNumber}`}
        // Above page loader backdrop (tooltip+1) so confirm stays clickable.
        style={{ zIndex: 1600 }}
      >
        <DialogTitle
          id={`delete_vehicle_dialog_title_${fieldId ?? 'unknown'}_${vehicleNumber}`}
        >
          Delete Permit?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this {vehicleTitle.toLowerCase()}{' '}
            permit? This action cannot be undone.
          </DialogContentText>
          <DialogContentText style={{ marginTop: 8 }}>
            ¿Está seguro de que desea eliminar este permiso de{' '}
            {String(vehicleTitle).toLowerCase() === 'guest vehicle'
              ? 'vehículo de visitante'
              : 'vehículo'}
            ? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            id={`cancel_delete_vehicle_button_${fieldId ?? 'unknown'}_${vehicleNumber}`}
            type="button"
            onClick={() => setDeleteDialogOpen(false)}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            id={`confirm_delete_vehicle_button_${fieldId ?? 'unknown'}_${vehicleNumber}`}
            type="button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleConfirmDelete();
            }}
            color="secondary"
            variant="contained"
            disabled={submitDisabled}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <input
        name={inputName('id')}
        type="hidden"
        ref={register}
        defaultValue={viewValues.id ?? ''}
      />
      {!isEditing ? (
        <input
          name={inputName('issue_date')}
          type="hidden"
          ref={register}
          defaultValue={viewValues.issue_date || today}
        />
      ) : null}
      <input
        name={inputName('licence_state')}
        type="hidden"
        ref={register}
        defaultValue={licenceStateCode}
      />
      {isEditing ? (
      <Box className={classes.editBody}>
        <Box className={classes.fieldRow}>
          <PermitFieldLabel className={classes.fieldLabel}>
            Status:
          </PermitFieldLabel>
          <Box className={classes.fieldControl}>
            <UnitStatusMetaField
              id={`vehicle_status_select_${fieldId ?? 'unknown'}_${vehicleNumber}`}
              name={inputName('status')}
              control={control}
              options={statusSelect}
              status={viewValues.status}
              editable
              fullWidth
            />
          </Box>
        </Box>
        <Box className={classes.fieldRow}>
          <PermitFieldLabel className={classes.fieldLabel}>
            Issue Date:
          </PermitFieldLabel>
          <Box className={classes.fieldControl}>
            <VppDatePicker
              id={`vehicle_issue_date_picker_${fieldId ?? 'unknown'}_${vehicleNumber}`}
              name={inputName('issue_date')}
              size="small"
              control={control}
              disabled
              error={errorCheck('issue_date')}
              helperText={errorText('issue_date')}
              defaultValue={viewValues.issue_date || today}
              fullWidth
              inputVariant="outlined"
            />
          </Box>
        </Box>
        <Box className={classes.fieldRow}>
          <PermitFieldLabel className={classes.fieldLabel}>
            Expiration Date:
          </PermitFieldLabel>
          <Box className={classes.fieldControl}>
            <VppDatePicker
              id={`vehicle_expiration_date_picker_${fieldId ?? 'unknown'}_${vehicleNumber}`}
              name={inputName('expiration_date')}
              size="small"
              control={control}
              disabled={disabled}
              error={errorCheck('expiration_date')}
              helperText={errorText('expiration_date')}
              defaultValue={
                viewValues.expiration_date || defaulteExpirationDateValue
              }
              fullWidth
              inputVariant="outlined"
            />
          </Box>
        </Box>
        <Box className={classes.fieldRow}>
          <Tooltip
            title="please enter letters and number only for your license plate example: ABC123"
            placement="top"
          >
            <PermitFieldLabel className={classes.fieldLabel}>
              License Plate:
            </PermitFieldLabel>
          </Tooltip>
          <Box className={classes.fieldControl}>
            <TextField
              margin="none"
              {...register(inputName('licence_plate'), licencePlateValidator)}
              name={inputName('licence_plate')}
              id={`vehicle_licence_plate_textfield_${fieldId ?? 'unknown'}_${vehicleNumber}`}
              size="small"
              defaultValue={viewValues.licence_plate ?? ''}
              disabled={disabled}
              inputRef={register}
              onChange={(event) => {
                control.fieldsRef.current[
                  inputName('licence_plate')
                ].ref.value = event.target.value
                  .toUpperCase()
                  .replaceAll(/[\W]/g, '');
              }}
              autoComplete="off"
              error={errorCheck('licence_plate')}
              helperText={errorText('licence_plate')}
              fullWidth
            />
          </Box>
        </Box>
        <Box className={classes.fieldRow}>
          <PermitFieldLabel className={classes.fieldLabel}>
            Licence State:
          </PermitFieldLabel>
          <Box className={classes.fieldControl}>
            <Autocomplete
              id={`vehicle_licence_state_autocomplete_${fieldId ?? 'unknown'}_${vehicleNumber}`}
              options={Object.keys(US_STATES_WITH_INITIALS)}
              getOptionLabel={(option) =>
                US_STATES_WITH_INITIALS[option] || option || ''
              }
              getOptionSelected={(option, value) => option === value}
              value={licenceStateCode || null}
              onChange={(event, newValue) => {
                setValue(
                  inputName('licence_state'),
                  normalizeLicenceState(newValue)
                );
              }}
              disabled={disabled}
              renderInput={renderAutocompleteTextField}
            />
          </Box>
        </Box>
        <Box className={classes.fieldRow}>
          <PermitFieldLabel className={classes.fieldLabel}>
            Make:
          </PermitFieldLabel>
          <Box className={classes.fieldControl}>
            <TextField
              margin="none"
              id={`vehicle_make_textfield_${fieldId ?? 'unknown'}_${vehicleNumber}`}
              name={inputName('make')}
              size="small"
              defaultValue={viewValues.make ?? ''}
              disabled={disabled}
              inputRef={register}
              error={errorCheck('make')}
              helperText={errorText('make')}
              fullWidth
            />
          </Box>
        </Box>
        <Box className={classes.fieldRow}>
          <PermitFieldLabel className={classes.fieldLabel}>
            Model:
          </PermitFieldLabel>
          <Box className={classes.fieldControl}>
            <TextField
              margin="none"
              id={`vehicle_model_textfield_${fieldId ?? 'unknown'}_${vehicleNumber}`}
              name={inputName('model')}
              size="small"
              defaultValue={viewValues.model ?? ''}
              disabled={disabled}
              inputRef={register}
              error={errorCheck('model')}
              helperText={errorText('model')}
              fullWidth
            />
          </Box>
        </Box>
        <Box className={`${classes.fieldRow} ${classes.fieldRowLast}`}>
          <PermitFieldLabel className={classes.fieldLabel}>
            Color:
          </PermitFieldLabel>
          <Box className={classes.fieldControl}>
            <TextField
              margin="none"
              id={`vehicle_color_textfield_${fieldId ?? 'unknown'}_${vehicleNumber}`}
              name={inputName('color')}
              size="small"
              defaultValue={viewValues.color ?? ''}
              disabled={disabled}
              inputRef={register}
              error={errorCheck('color')}
              helperText={errorText('color')}
              fullWidth
            />
          </Box>
        </Box>
      </Box>
      ) : (
        <PermitCardView
          key={`${type}-view-${vehicleIndex}-${isUnitLoaded ? 'loaded' : 'loading'}`}
          values={viewValues}
        />
      )}
      <Box mt="auto">
        <PermitCardActions
          isEditing={isEditing}
          onEdit={handleStartEdit}
          onCancel={() => {
            setIsEditing(false);
            onCancel();
          }}
          onSave={async () => {
            try {
              await onSave();
              setIsEditing(false);
            } catch {
              // Stay in edit mode when validation or save fails.
            }
          }}
          showActions={showActions}
          submitDisabled={submitDisabled}
          editButtonId={`${type}_permit_edit_button_${vehicleNumber}`}
          saveButtonId={`${type}_permit_save_button_${vehicleNumber}`}
          cancelButtonId={`${type}_permit_cancel_button_${vehicleNumber}`}
        />
      </Box>
    </Box>
  );
};

export default UnitsVehiclesBlock;
